import Button from 'components/form/Button';
import InputOTP from 'components/form/InputOTP';
import TextInput from 'components/form/TextInput';
import Modal from 'components/Layout/Modal';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleSendCertificateDownloadOtp, setApiError } from 'store/slices';
import { icons } from 'utils/constants';
import {
  convertString,
  formatTime,
  getCountryCode,
  getDataFromLocalStorage,
  numberOnlyFromInput,
  objectToFormData,
} from 'utils/helpers';
import * as Yup from 'yup';

const CertificateOtpPopup = ({ onHide, handleVerifyOtp, otpVerification }) => {
  const dispatch = useDispatch();

  const [formType, setFormType] = useState(0); // 0=choose method, 1=enter OTP, 2=WhatsApp confirm
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [otpLoader, setOtpLoader] = useState(false);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [resetOTP, setResetOTP] = useState(true);
  const [otpId, setOtpId] = useState('');
  const [otp, setOTP] = useState('');
  const [whatsappData, setWhatsappData] = useState({});
  const [counter, setCounter] = useState(600); // 600 sec = 10 min

  const emailFromStorage = getDataFromLocalStorage('email_id');

  const initialValues = {
    selectedMethod: null,
    email: emailFromStorage || '',
    whatsapp_number: '',
    country_code: 'IN',
    mobile_no_length: 10,
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().when('selectedMethod', {
      is: (val) => val !== 'whatsapp',
      then: (schema) => schema.required('Email is required.').email('Email must be a valid email'),
      otherwise: (schema) => schema,
    }),
    whatsapp_number: Yup.string().when('selectedMethod', {
      is: 'whatsapp',
      then: Yup.lazy((_, obj) => {
        const { country_code, mobile_no_length } = obj?.parent || {};
        if (country_code === 'IN' && mobile_no_length) {
          const validLengths = Array.isArray(mobile_no_length)
            ? mobile_no_length
            : [mobile_no_length];

          return Yup.string()
            .required('Whatsapp number is required.')
            .test(
              'valid-length',
              `Whatsapp number must be ${validLengths.join(' or ')} digits.`,
              (value) => value && validLengths.includes(value.length)
            );
        } else {
          return Yup.string()
            .required('Whatsapp number is required.')
            .min(8, 'Whatsapp number must be at least 8 digits.')
            .max(15, 'Whatsapp number must be at most 15 digits.');
        }
      }),
      otherwise: Yup.string().nullable(),
    }),
  });

  const handleSelectMethod = (method, setFieldValue) => {
    setFieldValue('selectedMethod', method);
    setSelectedMethod(method);
  };

  const handleSendOTP = async (values) => {
    setOtpLoader(true);
    const formData = objectToFormData({
      email: values?.email,
      phone_no: values?.whatsapp_number,
      country_code: values?.country_code,
      send_via: selectedMethod === 'whatsapp' ? 2 : 1,
    });

    const response = await dispatch(handleSendCertificateDownloadOtp(formData));
    if (response?.status === 200) {
      setIsOTPSent(true);
      setOtpId(response?.data?.otp_id);
      setResetOTP(true);
      setFormType(1);
      setCounter(600); // reset to 10 min
      dispatch(
        setApiError({
          show: true,
          message: 'OTP Sent Successfully.',
          type: 'success',
        })
      );
    }
    setOtpLoader(false);
  };

  const handleVerify = async () => {
    await handleVerifyOtp(otp, otpId);
  };

  const handleOtpChange = (newOtp) => {
    if (newOtp) setOTP(newOtp);
  };

  const handleSendOtpStep = (values, step) => {
    setFormType(step);
    setWhatsappData(values);
  };

  // countdown timer
  useEffect(() => {
    if (counter > 0) {
      const timer = setTimeout(() => setCounter(counter - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setIsOTPSent(false);
    }
  }, [counter]);


  return (
    <Modal
      titleBesideClose="Certificate Download"
      onHide={onHide}
      isTitleBorder
      isTitleVerticalCenter
      size="md"
      titleClass="text-16-300"
    >
      <div
        style={{
          maxWidth: '382px',
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div className="cmt-37 text-24-500-30 cmb-27">
          {formType === 2
            ? 'WhatsApp Verfication'
            : formType === 1
            ? 'Enter OTP'
            : selectedMethod === 'gmail'
            ? 'Gmail Verfication'
            : selectedMethod === 'whatsapp'
            ? 'WhatsApp Verfication'
            : 'Verify OTP'}
        </div>

        {/* Step 0 - Select method */}
        {formType === 0 && (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSendOTP}
          >
            {({ values, errors, handleChange, handleSubmit, setFieldValue, validateForm, setTouched }) => (
              <form onSubmit={handleSubmit} onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}>
                <div className="text-16-400 color-black-olive cmb-27 text-center">
                  Choose how you’d like to verify your account to download certificate : via Gmail or WhatsApp
                </div>

                {/* Email input */}
                <div className="mb-4">
                  <TextInput
                    id="email"
                    label="Email ID"
                    labelClass="mb-2"
                    placeholder="Enter email"
                    value={values.email}
                    error={errors.email}
                    onChange={(e) => handleChange(convertString(1, e))}
                    disabled={selectedMethod || emailFromStorage}
                  />
                </div>

                {/* WhatsApp input */}
                {selectedMethod === 'whatsapp' && (
                  <div className="mb-4">
                    <TextInput
                      isPhoneNumber
                      type="number"
                      id="whatsapp_number"
                      placeholder="Whatsapp Number"
                      value={values.whatsapp_number}
                      error={errors.whatsapp_number}
                      phoneNumberData={{ id: 'country_code', value: values.country_code }}
                      onChange={(e) => {
                        if (e.target.id === 'whatsapp_number') {
                          handleChange(numberOnlyFromInput(e));
                        } else {
                          handleChange(e);
                          setFieldValue('whatsapp_number', '');
                          setFieldValue('mobile_no_length', e.target.length);
                        }
                      }}
                    />
                  </div>
                )}

                {/* Buttons */}
                {selectedMethod ? (
                  <div className="d-flex gap-3 mb-5 w-100">
                    <div style={{ flex: '0 0 40%' }}>
                      <Button
                        text="Send OTP"
                        btnStyle="primary-dark"
                        onClick={async () => {
                          if (selectedMethod === 'whatsapp') {
                            const errors = await validateForm();
                            if (!Object.keys(errors).length) {
                              handleSendOtpStep(values, 2);
                            } else {
                              setTouched({ whatsapp_number: true });
                            }
                          } else {
                            handleSubmit();
                          }
                        }}
                        disabled={otpLoader}
                        btnLoading={otpLoader}
                        className="h-35 w-100"
                      />
                    </div>
                    <div style={{ flex: '0 0 57%' }}>
                      <Button
                        text="Cancel"
                        btnStyle="primary-light-outline"
                        onClick={() => {
                          setSelectedMethod(null);
                          setFieldValue('selectedMethod', null);
                        }}
                        className="h-35 w-100"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="d-flex gap-3 mb-5 w-100">
                    <div style={{ flex: '0 0 40%' }}>
                      <Button
                        text="GMAIL"
                        btnStyle="primary-light-outline"
                        icon={<span className="cme-10"><img src={icons.gmailSquare} height={16} width={16} /></span>}
                        onClick={async () => {
                          const isValid = await validateForm();
                          if (!Object.keys(isValid).length) {
                            handleSelectMethod('gmail', setFieldValue);
                          } else {
                            setTouched({ email: true });
                          }
                        }}
                        disabled={otpLoader}
                        btnLoading={otpLoader}
                        className="h-35 w-100 text-14-500-18"
                      />
                    </div>
                    <div style={{ flex: '0 0 57%' }}>
                      <Button
                        text="Whatsapp"
                        btnStyle="primary-whatsapp-outline"
                        icon={<span className="cme-10"><img src={icons.whatsAppChat} height={16} width={16} /></span>}
                        onClick={async () => {
                          const isValid = await validateForm();
                          if (!Object.keys(isValid).length) {
                            handleSelectMethod('whatsapp', setFieldValue);
                          } else {
                            setTouched({ email: true });
                          }
                        }}
                        className="h-35 w-100 text-14-500-18"
                      />
                    </div>
                  </div>
                )}
              </form>
            )}
          </Formik>
        )}

        {/* Step 1 - Enter OTP */}
        {formType === 1 && (
          <Formik initialValues={{}} onSubmit={handleVerify}>
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit} onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}>
                <div className="center-flex flex-column">
                  <InputOTP
                    blockSize={6}
                    resetOTP={resetOTP}
                    onSubmit={handleOtpChange}
                    setResetOTP={setResetOTP}
                    isDisableTyping={otpVerification || otpLoader}
                  />
                </div>
                <div className="cmt-27 mb-2">
                  {isOTPSent && <div className="text-center text-14-400">OTP sent!</div>}
                  {counter === 0 ? (
                    <div className="d-flex justify-content-center align-items-center gap-3">
                      <Button
                        className="ps-3 pe-3 h-35 text-14-400"
                        text="Resend OTP"
                        btnStyle="primary-outline"
                        onClick={() => {
                          handleSendOTP({
                            phone_no: whatsappData?.whatsapp_number,
                            country_code: whatsappData?.country_code,
                            email: whatsappData?.email,
                          });
                        }}
                        btnLoading={otpLoader}
                        disabled={otpLoader}
                      />
                    </div>
                  ) : (
                    <div className="text-center text-16-400 mt-2">
                      You can resend the OTP in
                      <span className="color-new-car text-16-500">{` ${formatTime(counter)}`}</span>
                    </div>
                  )}
                </div>
                <div className="d-flex justify-content-center gap-2 cmb-51 pt-3">
                  <Button
                    text="Verify to OTP"
                    btnStyle="primary-dark"
                    className="h-35 cps-20 cpe-20"
                    onClick={handleVerify}
                    disabled={otpVerification || otpLoader || !otp || otp.length !== 6}
                    btnLoading={otpVerification}
                  />
                  <Button
                    text="Cancel"
                    btnStyle="light-outline"
                    className="h-35 cps-20 cpe-20"
                    disabled={otpLoader}
                    onClick={onHide}
                  />
                </div>
              </form>
            )}
          </Formik>
        )}

        {/* Step 2 - WhatsApp confirmation */}
        {formType === 2 && (
          <div className="flex flex-col items-center justify-center text-center">
            <div className="cmb-24">
              <span className="text-16-500">
                {getCountryCode(whatsappData?.country_code || 'IN')} {whatsappData?.whatsapp_number}
              </span>
            </div>
            <div className="text-14-400 color-1717 cmb-24">
              We will send an OTP to your registered WhatsApp Number
            </div>
            <div className="d-flex justify-content-center gap-2 cmb-60">
              <Button
                text="Proceed"
                btnStyle="primary-dark"
                className="h-35 cps-20 cpe-20"
                onClick={() => handleSendOTP(whatsappData)}
                disabled={otpLoader}
                btnLoading={otpLoader}
              />
              <Button
                text="Cancel"
                btnStyle="light-outline"
                className="h-35 cps-20 cpe-20"
                disabled={otpLoader}
                onClick={() => {
                  setFormType(0);
                  setSelectedMethod(null);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CertificateOtpPopup;
